import { FC } from "react";

import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { ChevronUpIcon, EyeNotVisibleIcon, VerifiedIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectAssessmentsByPageId } from "store/selectors";

interface PublishActionButtonProps {
  onPublishGrades: () => void;
  onUnpublishGrades: () => void;
  page: PageType;
  sectionUserId: string | null;
  isMobile: boolean;
}

export const PublishActionButton: FC<PublishActionButtonProps> = ({
  onPublishGrades,
  onUnpublishGrades,
  page,
  sectionUserId,
  isMobile,
}) => {
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, page.id));

  if (!page) return null;

  const isPublished = !!page.assessments_published_at;
  const isDisabled = !sectionUserId || (!isPublished && assessments.length === 0);
  const statusLabel = isPublished ? t("publishing.published") : t("publishing.unpublished");

  const menuButton = (
    <RoundedButton
      aria-label={isMobile ? statusLabel : undefined}
      className="group"
      kind="secondary"
    >
      <IconText
        iconStart={
          isPublished ? (
            <>
              <VerifiedIcon className="text-green group-hover:hidden" />
              <ChevronUpIcon className="hidden group-hover:block" />
            </>
          ) : (
            <>
              <EyeNotVisibleIcon className="group-hover:hidden" />
              <ChevronUpIcon className="hidden group-hover:block" />
            </>
          )
        }
        text={isMobile ? undefined : statusLabel}
      />
    </RoundedButton>
  );

  return (
    <Menu direction="top" menuButton={menuButton}>
      <MenuItem
        disabled={isDisabled}
        eventName={isPublished ? "Assessment - Unpublish" : "Assessment - Publish"}
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        onClick={isPublished ? onUnpublishGrades : onPublishGrades}
      >
        <IconText
          iconStart={
            isPublished ? (
              <EyeNotVisibleIcon />
            ) : (
              <VerifiedIcon className={!isDisabled ? "text-green" : undefined} />
            )
          }
          text={isPublished ? t("assessment.unpublish_now") : t("assessment.publish_now")}
        />
      </MenuItem>
      <MenuDivider />
      <div className="flex max-w-[250px] px-2 py-1 text-xs text-black-tint-55">
        {isPublished ? t("assessment.unpublish_now_tooltip") : t("assessment.publish_now_tooltip")}
      </div>
    </Menu>
  );
};
